import {ViewOptions} from 'backbone';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import {MapType} from 'constants/MapsConstants';
import {MiniMapState} from 'molbio/maps/MapStates';
import {MapSelectorProps} from 'molbio/maps/options/MapSelector';
import MiniMapOptions from 'molbio/maps/options/MiniMapOptions';
import SequenceOptionsView, {SequenceOptionsBaseProps} from 'molbio/maps/options/SequenceOptionsView';
import SimplifiedMiniMapOptions from 'molbio/maps/options/SimplifiedMiniMapOptions';
import {ZoomSliderProps} from 'molbio/maps/options/Sliders';
import {minimapPaginate} from 'molbio/pdf/Paginate';
import MiniMapTabState, {CutsiteFilter} from 'molbio/tabstates/MiniMapTabState';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {FeatureCollectionName} from 'settings/visibility-options/VisibilityOptions';
import * as vo from 'settings/visibility-options/VisibilityOptions';

export interface MiniMapSequenceOptionsProps extends SequenceOptionsBaseProps {
  onHiddenCutsitesLinkClick: () => void;
  cutsiteFilter: CutsiteFilter | null;
  zoomSliderProps: ZoomSliderProps;
  shownAnnotationsCount?: number;
  visibleAnnotationsCount?: number;
  // TODO(BNCH-52942): Warn when translations are automatically hidden, like we do for annotations.
  shownTranslationsCount?: number;
  visibleTranslationsCount?: number;
}

export class MiniMapTabOptionsView extends SequenceOptionsView<MiniMapState, MiniMapTabState> {
  previewSourceName = 'minimap';
  previewPaginate = minimapPaginate;

  trackedFeatureCollections(): Array<FeatureCollectionName> {
    const trackedFeatureCollections: Array<FeatureCollectionName> = [
      'annotations',
      'translations',
      'sequencePrimers',
    ];
    if (BenchlingConfig.PARTS_ENABLE_OVERLAPPING_PARTS) {
      trackedFeatureCollections.push('parts');
    }
    return trackedFeatureCollections;
  }

  getProps(): MiniMapSequenceOptionsProps {
    const sequence = this.mapstate.sequence();
    return {
      ...super.getProps(),
      onHiddenCutsitesLinkClick: () => {
        sequence.selection().trigger('cutsite');
        $('#restriction-panel .view-enzymes').focus();
        // This used to automatically close the popover also.
      },
      cutsiteFilter: this.model.get('cutsiteFilter'),
      shownAnnotationsCount: this.mapstate.get('shownA'),
      visibleAnnotationsCount: this.mapstate.get('visibleA'),
      shownTranslationsCount: this.mapstate.get('shownTranslations'),
      visibleTranslationsCount: this.mapstate.get('visibleTranslations'),
      zoomSliderProps: {
        min: this.mapstate.getMinZoom(),
        max: this.mapstate.getMaxZoom(),
        value: this.mapstate.getZoom(),
        step: this.mapstate.getStepZoom(),
        disabled: !this.mapstate.get('zoomEnabled'),
        onChange: (zoom: number) => this.mapstate.setZoom(zoom),
        zoomBigger: () => this.mapstate.zoomByFactor(2),
        zoomSmaller: () => this.mapstate.zoomByFactor(0.5),
      },
    };
  }

  handleBigSequence(): void {
    const isReallyBig = this.mapstate.sequence().isReallyBig();
    const cutsitesVisibilityOption = this.model.get('visibilityOptions').getOption(vo.CUTSITES.name)!;
    const orfsVisibilityOption = this.model.get('visibilityOptions').getOption(vo.ORFS.name)!;

    cutsitesVisibilityOption.set('disabled', isReallyBig, {noSave: true});
    orfsVisibilityOption.set('disabled', isReallyBig, {noSave: true});
    if (isReallyBig) {
      cutsitesVisibilityOption.set('visible', false, {noSave: true});
      orfsVisibilityOption.set('visible', false, {noSave: true});
    }
  }

  initialize(options?: ViewOptions<MiniMapTabState>): void {
    super.initialize(options);

    this.listenTo(
      this.mapstate,
      'change:zoom change:zoomEnabled change:stepZoom change:scale ' +
        'change:visibleA change:shownA change:totalA ' +
        'change:visibleTranslations change:shownTranslations change:totalTranslations',
      () => this.render()
    );
    this.listenTo(this.model, 'change:cutsiteFilter', () => this.render());
  }

  remove(): this {
    ReactDOM.unmountComponentAtNode(this.el);
    super.remove();
    return this;
  }

  render(): this {
    renderToDOM(<MiniMapOptions {...this.getProps()} />, this.el);
    return this;
  }
}

export type SimplifiedMiniMapSequenceOptionsProps = MiniMapSequenceOptionsProps & MapSelectorProps;

export class SimplifiedMiniMapTabOptionsView extends MiniMapTabOptionsView {
  getProps(): SimplifiedMiniMapSequenceOptionsProps {
    return {
      ...super.getProps(),
      mapType: MapType.MINIMAP,
      onSelectMapType: (mapType: MapType) => {
        // Emit special event that we can listen to from React.
        this.mapstate.sequence().trigger('changeMapType', mapType);
      },
      isCircular: this.mapstate.sequence().circular(),
    };
  }

  render(): this {
    renderToDOM(<SimplifiedMiniMapOptions {...this.getProps()} />, this.el);
    return this;
  }
}
